import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import api from '../../../services/api';
import alertService from '../../../utils/alert';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/currency';
import { Modal, Btn, INPUT, LABEL, localToday, r2, errMsg } from './ui';

// Credit note against one invoice: a single amount (discount / goodwill) or
// returned items. Tax is added on the server at each line's rate.
export default function CreditNoteModal({ invoice, onClose, onSaved }) {
  const balance = r2((invoice.totalAmount || 0) - (invoice.amountPaid || 0) - (invoice.amountCredited || 0));
  const [mode, setMode] = useState('amount');
  const [amount, setAmount] = useState('');
  const [lines, setLines] = useState([{ description: '', quantity: 1, unitPrice: '' }]);
  const [reason, setReason] = useState('return');
  const [date, setDate] = useState(localToday());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const c = invoice.currency;
  const net = mode === 'amount' ? r2(amount) : r2(lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0), 0));
  // A lump sum carries the invoice's average rate (split by rate on the
  // server); returned items carry their own line's rate.
  const taxable = (invoice.subtotal || 0) - (invoice.discountAmount || 0);
  const avgRate = taxable > 0 ? ((invoice.taxAmount || 0) / taxable) * 100 : (invoice.taxRate || 0);
  const lineTax = (l) => (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0) * ((l.taxRate === '' || l.taxRate == null ? (invoice.taxRate || 0) : Number(l.taxRate)) / 100);
  const approxTotal = mode === 'amount' ? r2(net * (1 + avgRate / 100)) : r2(net + lines.reduce((s, l) => s + lineTax(l), 0));
  const invoiceLines = (invoice.lineItems || []).filter((l) => l.description);
  const pickLine = (i, idx) => {
    const src = invoiceLines[Number(idx)];
    if (!src) return;
    setLines(lines.map((x, j) => (j === i ? { ...x, description: src.description, hsnCode: src.hsnCode || '', unitPrice: String(src.unitPrice), taxRate: src.appliedRate ?? src.taxRate ?? '' } : x)));
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { reason, date, notes, ...(mode === 'amount' ? { amount: Number(amount) } : { lineItems: lines.map((l) => ({ ...l, quantity: Number(l.quantity), unitPrice: Number(l.unitPrice), taxRate: l.taxRate === '' || l.taxRate == null ? null : Number(l.taxRate) })) }) };
      const { data } = await api.post(`/crm/invoices/${invoice._id}/credit-notes`, body);
      alertService.success('Credit note issued', `${data.creditNote.creditNoteNumber} for ${formatCurrency(data.creditNote.totalAmount, c)}`);
      onSaved();
    } catch (e) {
      alertService.error('Credit note not issued', errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Credit note · ${invoice.invoiceNumber}`} onClose={onClose} wide={mode === 'items'}
      footer={<><Btn onClick={onClose}>Cancel</Btn><Btn primary disabled={saving || !(net > 0) || approxTotal > balance + 0.01} onClick={save}>{saving ? 'Issuing…' : 'Issue credit note'}</Btn></>}>
      <p className="text-[13px] text-[#706E6B]">Still owed on this invoice: <strong className="text-[#2E2E2E] tabular-nums">{formatCurrency(balance, c)}</strong>. A credit note reduces that; for a fully paid invoice, record a refund instead.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Credit by">
        {[['amount', 'An amount'], ['items', 'Returned items']].map(([k, l]) => (
          <button type="button" key={k} role="radio" aria-checked={mode === k} onClick={() => setMode(k)}
            className={cn('h-9 rounded border text-[12px] font-semibold', mode === k ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white border-[#D8DDE6]')}>{l}</button>
        ))}
      </div>
      {mode === 'amount' ? (
        <div><label className={LABEL} htmlFor="cn-amt">Amount before tax</label><input id="cn-amt" type="number" min="0" step="0.01" className={INPUT} value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
      ) : (
        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-5">
                {invoiceLines.length > 0 && (
                  <select aria-label="Invoice line" className={cn(INPUT, 'mb-1')} value="" onChange={(e) => pickLine(i, e.target.value)}>
                    <option value="">From the invoice…</option>
                    {invoiceLines.map((src, k) => <option key={k} value={k}>{src.description}{src.appliedRate !== undefined && src.appliedRate !== null ? ` (${src.appliedRate}%)` : ''}</option>)}
                  </select>
                )}
                <input aria-label="Item" placeholder="Item returned" className={INPUT} value={l.description} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} />
              </div>
              <div className="col-span-2"><input aria-label="Quantity" type="number" min="0" step="0.01" className={INPUT} value={l.quantity} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))} /></div>
              <div className="col-span-2"><input aria-label="Rate" placeholder="Rate" type="number" min="0" step="0.01" className={INPUT} value={l.unitPrice} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, unitPrice: e.target.value } : x)))} /></div>
              <div className="col-span-2"><input aria-label="GST %" placeholder={`${invoice.taxRate || 0}%`} type="number" min="0" max="100" step="0.01" className={INPUT} value={l.taxRate ?? ''} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, taxRate: e.target.value } : x)))} /></div>
              <div className="col-span-1">{lines.length > 1 && <button type="button" aria-label="Remove item" onClick={() => setLines(lines.filter((_, j) => j !== i))} className="p-2 text-[#706E6B]"><Trash2 size={14} /></button>}</div>
            </div>
          ))}
          <button type="button" onClick={() => setLines([...lines, { description: '', quantity: 1, unitPrice: '' }])} className="text-[12px] font-semibold text-[#0176D3] flex items-center gap-1"><Plus size={14} /> Add item</button>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={LABEL} htmlFor="cn-reason">Reason</label>
          <select id="cn-reason" className={INPUT} value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="return">Goods returned</option><option value="discount">Discount</option><option value="correction">Billing correction</option><option value="other">Other</option>
          </select>
        </div>
        <div><label className={LABEL} htmlFor="cn-date">Date</label><input id="cn-date" type="date" className={INPUT} value={date} onChange={(e) => setDate(e.target.value)} /></div>
      </div>
      <div><label className={LABEL} htmlFor="cn-notes">Notes</label><input id="cn-notes" className={INPUT} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
      <p className={cn('text-[13px]', approxTotal > balance + 0.01 ? 'text-[#BA0517]' : 'text-[#2E2E2E]')}>
        About <strong className="tabular-nums">{formatCurrency(approxTotal, c)}</strong> including tax at the invoice's rate{approxTotal > balance + 0.01 ? ' — more than is still owed.' : '.'}
      </p>
    </Modal>
  );
}
